import { inject, Injectable, signal, computed } from '@angular/core';
import { Observable, switchMap, tap, map } from 'rxjs';
import { BreakpointObserver } from '@angular/cdk/layout';
import { toSignal } from '@angular/core/rxjs-interop';

import { FormManagementEndpointService } from '../core/api/services/form-management-endpoint.service';
import { RadarEndpointService } from '../core/api/services/radar-endpoint.service';
import { BorrowerProfile, ContactInfo, LoanInfo, PropertyInfo } from '../core/api/models/form-management.model';

@Injectable({
  providedIn: 'root'
})
export class FormService {
  private formManagementEndpointService = inject(FormManagementEndpointService);
  private radarEndpointService = inject(RadarEndpointService);
  private breakpointObserver = inject(BreakpointObserver);

  currentStep = signal<number>(1);
  rejectReason = signal<string | null>(null);
  userUuid = signal<string | null>(null);
  ownerOccupiedPercentage = signal<boolean | null>(null);
  siteSource = signal<string | null>(null);
  role = signal<string | null>(null);
  manager = signal<string | null>(null);
  loanPurpose = signal<string | null>(null);

  // Prefill values coming from query params on portal
  prefillEmail = signal<string | null>(null);
  prefillFirstname = signal<string | null>(null);
  prefillLastname = signal<string | null>(null);

  private _mobileBreakpoint = toSignal(
    this.breakpointObserver.observe('(max-width: 1023px)'),
    { initialValue: { matches: false, breakpoints: {} } }
  );

  isMobile = computed(() => this._mobileBreakpoint()?.matches ?? false);

  termsAndPolicy = {
    avanacompaniesTerms: 'https://www.avanacompanies.com/terms-and-conditions',
    avanacompaniesPrivacy: 'https://www.avanacompanies.com/privacy-policy',
    avanaCapitalTerms: 'https://avanacapital.com/terms-conditions/',
    avanaCapitalPrivacy: 'https://avanacapital.com/privacy-policy/',
    avanaCusoTerms: 'https://www.avanacompanies.com/terms-and-conditions',
    avanaCusoPrivacy: 'https://avanacuso.com/privacy-policy/'
  };

  initiateForm(body: ContactInfo): Observable<void> {
    return this.formManagementEndpointService.initiateForm(body.email).pipe(
      tap((res) => {
        this.userUuid.set(res.uuid);
        localStorage.setItem('userUuid', res.uuid);
      }),
      switchMap(() => {
        return this.formManagementEndpointService.updateContactInfo(this.userUuid()!, body);
      }),
      map(() => {
        this.currentStep.set(2);
      })
    );
  }

  getContactInfo() {
    return this.formManagementEndpointService.getContactInfo(this.userUuid()!);
  }

  updateContactInfo(body: ContactInfo) {
    return this.formManagementEndpointService.updateContactInfo(this.userUuid()!, body);
  }

  getLoanInfo() {
    return this.formManagementEndpointService.getLoanInfo(this.userUuid()!);
  }

  updateLoanInfo(body: LoanInfo) {
    return this.formManagementEndpointService.updateLoanInfo(this.userUuid()!, body);
  }

  getPropertyInfo() {
    return this.formManagementEndpointService.getPropertyInfo(this.userUuid()!);
  }

  updatePropertyInfo(body: PropertyInfo) {
    return this.formManagementEndpointService.updatePropertyInfo(this.userUuid()!, body);
  }

  getBorrowerProfile() {
    return this.formManagementEndpointService.getBorrowerProfile(this.userUuid()!);
  }

  updateBorrowerProfile(body: BorrowerProfile) {
    return this.formManagementEndpointService.updateBorrowerProfile(this.userUuid()!, body);
  }

  getHotelBrands() {
    return this.formManagementEndpointService.getHotelBrands();
  }

  submitForm(formData: FormData) {
    return this.formManagementEndpointService.submitForm(this.userUuid()!, formData);
  }

  formExist(userUuid: string) {
    return this.formManagementEndpointService.formExist(userUuid);
  }

  validateEmail(email: string) {
    return this.formManagementEndpointService.validateEmail(email);
  }

  getPropertyAddressOptions(query: string) {
    return this.radarEndpointService.getAddressOptions(query);
  }

  getCookie(name: string): string | null {
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? decodeURIComponent(match[2]) : null;
  }
}
